import * as Sentry from '@sentry/react';
import jwtDecode from 'jwt-decode';
import { useCookies } from 'react-cookie';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';
import { JwtToken } from 'types/dashboard.d';

export const TOKEN_NAME = 'accessToken';

// custom hook to handle authToken - we use compositon to decouple the auth system and it's storage
export const useAuthToken = (): [
  string,
  (authToken: string) => void,
  () => void,
  () => Maybe<JwtToken>,
] => {
  // we use react-cookies to access our cookies
  const [cookies, setCookie, removeCookie] = useCookies([TOKEN_NAME]);

  // this function allows to save any string in our cookies, under the key "authToken"
  const setAuthToken = (authToken: string) => {
    return setCookie(TOKEN_NAME, authToken, { path: '/' });
  };

  // this function removes the key "authToken" from our cookies. Useful to logout
  const removeAuthToken = () => {
    Sentry.configureScope((scope) => scope.setUser(null));
    return removeCookie(TOKEN_NAME, { path: '/' });
  };

  // get decoded values from the JWT token; empty if no auth token
  const getDecodedToken = (): Maybe<JwtToken> => {
    let decodedToken;
    try {
      decodedToken = jwtDecode(cookies[TOKEN_NAME]) as JwtToken;
    } catch {
      decodedToken = null; // can be empty if not auth'd
    }
    return decodedToken;
  };

  return [cookies[TOKEN_NAME], setAuthToken, removeAuthToken, getDecodedToken];
};
